import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Ticket, Plus, Copy, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import './Cupons.css';

const Cupons = () => {
  const [modalAberto, setModalAberto] = useState(false);
  const queryClient = useQueryClient();

  const { data: cupons } = useQuery(
    'cupons',
    async () => {
      const estabelecimento = queryClient.getQueryData('estabelecimento')?.data?.estabelecimento?._id;
      if (!estabelecimento) return { cupons: [] };
      const response = await api.get(`/api/cupons?estabelecimento=${estabelecimento}`);
      return response.data.data;
    }
  );

  const copiarCodigo = (codigo) => {
    navigator.clipboard.writeText(codigo);
    toast.success('Código copiado!');
  };

  return (
    <div className="cupons-page">
      <div className="page-header">
        <div>
          <h2><Ticket size={28} /> Cupons</h2>
          <p>Gerencie cupons de desconto</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModalAberto(true)}>
          <Plus size={20} />
          Novo Cupom
        </button>
      </div>

      <div className="cupons-grid">
        {cupons?.cupons?.map(cupom => {
          const agora = new Date();
          const inicio = new Date(cupom.dataInicio);
          const fim = new Date(cupom.dataFim);
          const valido = agora >= inicio && agora <= fim && cupom.ativo;

          return (
            <div key={cupom._id} className={`cupom-card ${valido ? 'ativo' : 'inativo'}`}>
              <div className="cupom-header">
                <div>
                  <h3>{cupom.codigo}</h3>
                  <p>{cupom.descricao || 'Sem descrição'}</p>
                </div>
                <button
                  className="btn-icon"
                  onClick={() => copiarCodigo(cupom.codigo)}
                >
                  <Copy size={16} />
                </button>
              </div>
              <div className="cupom-info">
                <div>
                  <span>Tipo</span>
                  <strong>
                    {cupom.tipo === 'desconto_percentual' && `${cupom.valor}% OFF`}
                    {cupom.tipo === 'desconto_fixo' && `R$ ${cupom.valor.toFixed(2)} OFF`}
                    {cupom.tipo === 'frete_gratis' && 'Frete Grátis'}
                  </strong>
                </div>
                <div>
                  <span>Validade</span>
                  <strong>{new Date(cupom.dataFim).toLocaleDateString('pt-BR')}</strong>
                </div>
                <div>
                  <span>Uso</span>
                  <strong>
                    {cupom.quantidadeUsada} / {cupom.quantidadeTotal || '∞'}
                  </strong>
                </div>
              </div>
              <div className="cupom-status">
                {valido ? (
                  <span className="status-ativo">
                    <CheckCircle size={16} />
                    Válido
                  </span>
                ) : (
                  <span className="status-inativo">
                    <XCircle size={16} />
                    Inválido
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modalAberto && (
        <ModalCupom onClose={() => setModalAberto(false)} />
      )}
    </div>
  );
};

const ModalCupom = ({ onClose }) => {
  const [formData, setFormData] = useState({
    codigo: '',
    tipo: 'desconto_percentual',
    valor: '',
    valorMinimo: '',
    dataInicio: new Date().toISOString().split('T')[0],
    dataFim: '',
    quantidadeTotal: '',
    ativo: true
  });
  const queryClient = useQueryClient();

  const saveMutation = useMutation(
    (data) => api.post('/api/cupons', data),
    {
      onSuccess: () => {
        toast.success('Cupom criado!');
        queryClient.invalidateQueries('cupons');
        onClose();
      }
    }
  );

  const gerarCodigo = () => {
    const codigo = Math.random().toString(36).substring(2, 10).toUpperCase();
    setFormData({ ...formData, codigo });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const estabelecimento = queryClient.getQueryData('estabelecimento')?.data?.estabelecimento?._id;
    saveMutation.mutate({
      ...formData,
      estabelecimento,
      valor: parseFloat(formData.valor),
      valorMinimo: parseFloat(formData.valorMinimo) || 0,
      quantidadeTotal: formData.quantidadeTotal ? parseInt(formData.quantidadeTotal) : null,
      dataInicio: new Date(formData.dataInicio),
      dataFim: new Date(formData.dataFim)
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Novo Cupom</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Código</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                required
                placeholder="Ex: PROMO10"
              />
              <button type="button" className="btn btn-secondary" onClick={gerarCodigo}>
                Gerar
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Tipo</label>
            <select
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              required
            >
              <option value="desconto_percentual">Desconto Percentual</option>
              <option value="desconto_fixo">Desconto Fixo</option>
              <option value="frete_gratis">Frete Grátis</option>
            </select>
          </div>
          <div className="form-group">
            <label>Valor</label>
            <input
              type="number"
              step="0.01"
              value={formData.valor}
              onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
              required
              placeholder={formData.tipo === 'desconto_percentual' ? 'Ex: 10 (10%)' : 'Ex: 5.00'}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Data Início</label>
              <input
                type="date"
                value={formData.dataInicio}
                onChange={(e) => setFormData({ ...formData, dataInicio: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Data Fim</label>
              <input
                type="date"
                value={formData.dataFim}
                onChange={(e) => setFormData({ ...formData, dataFim: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>Quantidade Total (deixe vazio para ilimitado)</label>
            <input
              type="number"
              value={formData.quantidadeTotal}
              onChange={(e) => setFormData({ ...formData, quantidadeTotal: e.target.value })}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              Criar Cupom
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Cupons;

